// @ core
const apiURLBuilder = require('core/apiURLBuilder');
const deburr = require('lodash/string/deburr');
// @ helpers
const { APIGet } = require('utilities/APIRequestHelper');
const DateHelper = require('utilities/dateHelper');
// @ constants
const {
    CARD_HISTORY_REQUEST_IN_PROGRESS,
    CARD_HISTORY_REQUEST_SUCCESS,
    CARD_HISTORY_REQUEST_FAILURE,
    CARD_HISTORY_SET_VALUE
} = require('constants/contactCenter/actionTypes');

const {
    CARD_HISTORY_FIELD_HIRE_DATE,
    CARD_HISTORY_FIELD_ACTIVATION_DATE,
    CARD_HISTORY_FIELD_DELIVERY_DATE,
    CARD_HISTORY_FIELD_STAMP_PENDING_DATE,
    CARD_HISTORY_FIELD_PREVIOUS_CARD_LIMIT,
    CARD_HISTORY_FIELD_LAST_EDIT_DATE,
    CARD_HISTORY_FIELD_PIN_EDIT_DATE_ATM,
    CARD_HISTORY_FIELD_PIN_EDIT_DATE_WEB_APP,
    CARD_HISTORY_FIELD_PIN_RESET_DATE,
    CARD_HISTORY_FIELD_BLOCKING_DATE,
    CARD_HISTORY_FIELD_WITHDRAWAL_DATE
} = require('constants/contactCenter/latestCardHistory');

/**
 * eventsDefinition keeps the relationship between the events from the card history services
 * and the required fields from each event
 * @type {Object}
 */
const eventsDefinitionCard = {
    'AL': {
        fields: {
            time: CARD_HISTORY_FIELD_HIRE_DATE,
        }
    },
    'CP': {
        fields: {
            time: CARD_HISTORY_FIELD_PIN_EDIT_DATE_ATM,
        }
    },
    'NS': {
        fields: {
            time: CARD_HISTORY_FIELD_PIN_EDIT_DATE_WEB_APP,
        }
    },
    'AT': {
        fields: {
            time: CARD_HISTORY_FIELD_ACTIVATION_DATE,
        }
    },
    'ED': {
        fields: {
            time: CARD_HISTORY_FIELD_DELIVERY_DATE,
        }
    },
    'PE': {
        fields: {
            time: CARD_HISTORY_FIELD_STAMP_PENDING_DATE,
        }
    },
    'PIN_RESET': { // Exceptional case
        fields: {
            time: CARD_HISTORY_FIELD_PIN_RESET_DATE,
        }
    },
    'BL': {
        fields: {
            time: CARD_HISTORY_FIELD_BLOCKING_DATE,
        }
    },
    'BJ': {
        fields: {
            time: CARD_HISTORY_FIELD_WITHDRAWAL_DATE,
        }
    },
    'ML': {
        fields: {
            descModuloProceso: CARD_HISTORY_FIELD_PREVIOUS_CARD_LIMIT,
            time: CARD_HISTORY_FIELD_LAST_EDIT_DATE
        }
    }
};
const eventsDefinitionContract = {
    'ALTA': {
        fields: {
            time: CARD_HISTORY_FIELD_HIRE_DATE,
        }
    },
    'CAMBIO DE NUMERO SECRETO (PIN)': {
        fields: {
            time: CARD_HISTORY_FIELD_PIN_EDIT_DATE_ATM,
        }
    },
    'PETICION REENVIO NUM.SECRETO': {
        fields: {
            time: CARD_HISTORY_FIELD_PIN_EDIT_DATE_WEB_APP,
        }
    },
    'ACTIVACION TARJETA': {
        fields: {
            time: CARD_HISTORY_FIELD_ACTIVATION_DATE,
        }
    },
    'ENVIADA A DISTRIBUCION': {
        fields: {
            time: CARD_HISTORY_FIELD_DELIVERY_DATE,
        }
    },
    'PENDIENTE DE ESTAMPAR': {
        fields: {
            time: CARD_HISTORY_FIELD_STAMP_PENDING_DATE,
        }
    },
    'PIN_RESET': { // Exceptional case
        fields: {
            time: CARD_HISTORY_FIELD_PIN_RESET_DATE,
        }
    },
    'BLOQUEO': {
        fields: {
            time: CARD_HISTORY_FIELD_BLOCKING_DATE,
        }
    },
    'BAJA': {
        fields: {
            time: CARD_HISTORY_FIELD_WITHDRAWAL_DATE,
        }
    },
    'MODIFICACION DE LIMITE': {
        fields: {
            descModuloProceso: CARD_HISTORY_FIELD_PREVIOUS_CARD_LIMIT,
            time: CARD_HISTORY_FIELD_LAST_EDIT_DATE
        }
    }
};

const requestLatestCardHistoryInProgress = () => ({
    type: CARD_HISTORY_REQUEST_IN_PROGRESS,
});

const requestLatestCardHistorySuccess = data => ({
    type: CARD_HISTORY_REQUEST_SUCCESS,
    payload: {
        data
    },
});

const setLatestCardHistoryValue = (key, value) => ({
    type: CARD_HISTORY_SET_VALUE,
    payload: {
        key,
        value
    }
})

const requestLatestCardHistoryFailure = error => ({
    type: CARD_HISTORY_REQUEST_FAILURE,
    payload: {
        error,
    },
});

/*
 * normalizeText transforms the input strings into expected keys according to the
 * events definition keys
 */
const normalizeText = (codTipoSuceso) => {
    return deburr(codTipoSuceso) // Removes diacritical marks
        .replace(/\*/g, '') // Removes asterisks
        .trim()
        .toUpperCase();
}

/*
 * getElement returns an event mapping definition according to the meta-data
 *   associated to each event returned by the services
 * according to the values of codTipoSuceso and descModuloProceso
 */

const getKeyOrGroup = (event, group, comparisonValue) => {
    const processModule = normalizeText(event.descModuloProceso);
    let key;
    if (group === comparisonValue && processModule.indexOf('RESETEO DE INTENTOS PIN VALOR', 0) !== -1) {
      key = 'PIN_RESET';
    } else {
      key = group;
    }
    return key;
};

const getElementContract = (event) => {
    const group = normalizeText(event.descGrupoCaracteristicas);
    const key = getKeyOrGroup(event, group, 'RESETEO INTENTO DE PIN');

    return key ? eventsDefinitionContract[key] : null;
}

const getElementCard = (event) => {
    const group = normalizeText(event.codTipoSuceso);
    const key = getKeyOrGroup(event, group, 'RP');

    return key ? eventsDefinitionCard[key] : null;
}

const parseEventTimes = (events, type) => events.reduce(
    (acc, event) => {
    const element = type === 'contract' ? getElementContract(event) : getElementCard(event);
    const additional = Object.assign({}, acc);

    if (!!element) {
        const fields = Object.keys(element.fields);
        const time = DateHelper.datetimeFromArrays(event.fechaSuceso, event.horaOperacion);

        // According to the event definition, each event might have multiple interesting fields
        // Records each field into the hashmap
        fields.forEach(field => {
            const key = element.fields[field];
            let value;
            // Time is not a regular field provided by the events list on the server
            //   but the conjunction of fechaSuceso and horaOperacion
            // For this reason it is a special case
            if (field === 'time') {
                value = time;
            } else {
                value = event[field];
            }
            // In case the event already exists, keep track of the latest one
            if (!additional[key] || new Date(time) > new Date(additional[key]['time']) ) {
                // For each event, provide its value and time (even if the value is already its time)
                additional[key] = {
                    value,
                    time
                }
            }
        });
    }
    return Object.assign({}, acc, additional);
}
, {});

const requestLatestCardHistory = (cardNumber, productNumber, contractNumber) => dispatch => {
    dispatch(requestLatestCardHistoryInProgress());

    const requestData = {
        cardNumber,
        productNumber,
        contractNumber,
    };

    const cardHistoryService = APIGet(dispatch, apiURLBuilder.getURL('cardHistory', requestData))

    // Promise for resolving the card history service call
    const promisedCardHistory = cardHistoryService
    .then(response => response.listaSucesosContr ? response.listaSucesosContr.sucesos : []);

    // Promise for resolving the card contract history service call
    const promisedContractHistory = APIGet(dispatch, apiURLBuilder.getURL('cardContractHistory', requestData))
    .then(response => response.listaSucesosContr ? response.listaSucesosContr.sucesos : []);

    // If both service resolve successfully
    Promise.all([promisedCardHistory, promisedContractHistory])
    .then(valuesLists => {
        // Join their events
        const events = {
            ...parseEventTimes(valuesLists[0], 'card'),
            ...parseEventTimes(valuesLists[1], 'contract'),
        };
        // Parse all the events and dispatch the hashmap
        dispatch(requestLatestCardHistorySuccess(events));
    })
    .catch(error => dispatch(requestLatestCardHistoryFailure(error)));

    cardHistoryService.then(response => {
        dispatch(setLatestCardHistoryValue('domicilio', response.domicilio))
    })
}

module.exports = {
    requestLatestCardHistory,
}
